import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setTreatmentViewData } from "../../../../app/patientSlice";

const DEBOUNCE_MS = 500;

export const useTreatmentSearch = ({ editData = [], getData }) => {
  const dispatch = useDispatch();
  const dropRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [open, setOpen] = useState(false);

  const treatmentViewData = useSelector((state) => state.patient.treatmentViewData);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    setOpen(Boolean(debouncedSearch.trim()));
  }, [debouncedSearch]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const allOptions = useMemo(() => {
    return editData
      .filter((item) => item.key !== "-1")
      .flatMap((item) =>
        item.options.map((opt) => ({
          ...opt,
          parentKey: item.key,
          parentName: item.name,
        }))
      );
  }, [editData]);

  const filteredOptions = useMemo(() => {
    if (!debouncedSearch.trim()) return [];
    const searchText = debouncedSearch.toLowerCase();
    return allOptions.filter(
      (opt) =>
        opt?.materialCode?.toLowerCase().includes(searchText) ||
        opt?.materialDescription?.toLowerCase().includes(searchText) ||
        opt?.productFamilyDesc?.toLowerCase().includes(searchText)
    );
  }, [debouncedSearch, allOptions]);

  const isSelected = useCallback(
    (materialNumber, parentKey) => {
      return treatmentViewData?.some((item) => item.key === parentKey && item.value === materialNumber);
    },
    [treatmentViewData]
  );

  const toggleItem = useCallback(
    (opt) => {
      const { materialNumber, parentKey, isRecommended } = opt;
      const currentSelected = treatmentViewData || [];

      const alreadySelected = currentSelected.some((item) => item.key === parentKey && item.value === materialNumber);

      const updated = alreadySelected
        ? currentSelected.filter((item) => !(item.key === parentKey && item.value === materialNumber))
        : [...currentSelected, { key: parentKey, value: materialNumber, isRecommended: isRecommended || 0 }];

      dispatch(setTreatmentViewData(updated));
      getData?.();
    },
    [treatmentViewData, dispatch, getData]
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    setDebouncedSearch("");
    setOpen(false);
  }, []);

  const selectedChips = useMemo(() => {
    return (treatmentViewData || [])
      .filter((item) => item.key !== "-1")
      .map((item) => {
        const opt = allOptions.find((o) => o.materialNumber === item.value && o.parentKey === item.key);
        return opt ? { ...item, opt } : null;
      })
      .filter(Boolean);
  }, [treatmentViewData, allOptions]);

  const handleRemove = useCallback(
    (materialNumber) => {
      const updated = (treatmentViewData || []).filter((item) => item.value !== materialNumber);
      dispatch(setTreatmentViewData(updated));
      getData?.();
    },
    [treatmentViewData, dispatch, getData]
  );

  const handleFocus = useCallback(() => {
    if (debouncedSearch.trim()) setOpen(true);
  }, [debouncedSearch]);

  return {
    dropRef,
    inputValue,
    open,
    filteredOptions,
    selectedChips,
    setInputValue,
    isSelected,
    toggleItem,
    handleClear,
    handleRemove,
    handleFocus,
  };
};
